// Import necessary elements from 'antd' and other utilities
import React from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { Space, Form as AntForm, Divider, Slider, Card, Radio,Typography, Tooltip } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import {RiArrowRightUpFill} from "@remixicon/react";
import styled from 'styled-components';

const { Text, Title, Paragraph, Link } = Typography; // For different text sizes


export const LearnMoreLink = styled.a`
  text-align: center;
  display: block;
  margin-top: 10px;
`;

const StyledRadioGroup = styled(Radio.Group)`
  .ant-radio-button-wrapper {
    padding: 0 30px; /* Increase padding for larger buttons */
    font-size: 20px; /* Larger font size for labels */
    height: 50px; /* Increase height for larger buttons */
    line-height: 50px; /* Center text vertically */
    text-transform: capitalize; /* Uppercase text */
  }

  .ant-radio-button-wrapper:not(:first-child)::before {
    display: none; /* Optionally remove the inner border */
  }

  /* Custom styles for the checked state */
  .ant-radio-button-wrapper-checked {
    /* Default styles for checked state */
  }

  /* Apply blue background for 'Yes' option when selected */
  .ant-radio-button-wrapper-checked:first-child {
    background-color: #4C9AFF; /* Adjust the color as needed */
    border:none;
    color: white; /* Change text color for better readability */
  }

  /* Apply red background for 'No' option when selected */
  .ant-radio-button-wrapper-checked:last-child {
    background-color: #000; /* Adjust the color as needed */
    border: none; /* Adjust the color as needed */
    color: white; /* Change text color for better readability */
  }
`;
const TITLE_STLYE = {
    fontSize: '25px',
    fontWeight: '500',
    fontFamily: 'Inter, sans-serif',
}
const TEXT_STYLE = {
    fontSize: `16px`,
    fontWeight: '400',
    fontFamily: 'Inter, sans-serif',
    color: '#333',
}

const TextWrapStyle = {
    maxWidth: '80%',
    margin: 'auto',
}


const AssetAllocation = ({ control }) => {
  return (
      <Card className={'form-card pt-5 pb-6 px-6'} bordered={false}>
          <Space direction="vertical" size="large">
            <Text style={TITLE_STLYE}>Choose an Equity Target Your Comfortable With</Text>
            <div style={TextWrapStyle}>
                  <Text style={TEXT_STYLE}>Use the slider below to set the desired portfolio allocations between equities and fixed income</Text>
              </div>
          </Space>
          <Divider />
          <AntForm.Item required>
            <Controller
                name="equity"
                control={control}
                render={({ field: { onChange, value } }) => {
                  // Ensure value is a number and within 0-100 range
                  const numericValue = typeof value === 'number' ? Math.min(Math.max(value, 0), 100) : 0;
                  const equity = numericValue;
                  const fixed = 100 - numericValue;

                  return (
                    <>
                      <div style={{ textAlign: 'center', marginBottom: '20px', padding: '20px', fontWeight: '600' }}>
                        {equity}% Equity / {fixed}% Fixed Income
                      </div>
                      <Slider
                        min={0}
                        max={100}
                        onChange={onChange}
                        value={numericValue}
                        tipFormatter={(value) => `${value}%`}
                        marks={{
                          0: '0%',
                          20: '20%',
                          40: '40%',
                          60: '60%',
                          80: '80%',
                          100: '100%',
                        }}
                        step={1}
                      />
                    </>
                  );
                }}
            />
          </AntForm.Item>
      </Card>
  );
};


const DecisionCard = ({ title, description, field }) => {
  return (
    <Card className={'form-card pt-5 pb-6 px-6'} bordered={false}>
      <Space direction="vertical" size="large">
        <Text style={TITLE_STLYE}>{title}</Text>
          <div style={TextWrapStyle}>
              <Text style={TEXT_STYLE}>{description}</Text>
          </div>
        <StyledRadioGroup {...field} optionType={'button'}>
          <Radio value={true}>Yes</Radio>
          <Radio value={false}>No</Radio>
        </StyledRadioGroup>
      </Space>
    </Card>
  );
};


const FlexibleDecisionCard = ({ title, description, field, options }) => {
  return (
    <Card className={'form-card pt-5 pb-6 px-6'} bordered={false}>
      <Space direction="vertical" size="large">
        <Text style={TITLE_STLYE}>{title}</Text>
          <div style={TextWrapStyle}>
              <Text style={TEXT_STYLE}>{description}</Text>
          </div>
          <StyledRadioGroup {...field} optionType={'button'}>
          {options.map((option) => (
            <Radio key={option.value} value={option.value}>{option.label}</Radio>
          ))}
        </StyledRadioGroup>
      </Space>
    </Card>
  );
};


const InternationalEquitiesYesNo = () => {
  const { control } = useFormContext();

  return (
    <Controller
      name="international"
      control={control}
      defaultValue={false}
      render={({ field }) => (
        <DecisionCard
            title="Include Foreign Equities in Portfolio"
            description="If selected, this will allow for portfolios with options to foreign securities (limited to ETFs only currently),
            which can but does not guarantee exposure to foreign equities at any time"
            field={field}
            learnMoreLink="#"
        />
      )}
    />
  );
};

const DividendIncomeYesNo = () => {
  const { control } = useFormContext();

  return (
    <Controller
      name="dividend"
      control={control}
      defaultValue={false}
      render={({ field }) => (
          <DecisionCard
                title="Include a Modern Income Strategy"
                description="Including a modern income strategy can be a great way to get some less risky single stock
                exposure that can be an excellent source for meeting distribution schedules"
                field={field}
                learnMoreLink="#"
          />
      )}
    />
  );
};
const SingleStockSMAYesNo = () => {
  const { control } = useFormContext();

  return (
    <Controller
      name="singleStock"
      control={control}
      defaultValue={false}
      render={({ field }) => (
        <DecisionCard
            title="Include a Single Stock SMA"
            description="For higher net worth individuals, single stock SMAs can provide amplified exposure to
            to an etf or group of etfs while maintaining a risk profile in line proportionally with its sectors"
            field={field}
            learnMoreLink="#"
        />
      )}
    />
  );
};

const PortfolioTilt = () => {
  const { control } = useFormContext();

  return (
    <Controller
      name="tilt"
      control={control}
      defaultValue="No Tilt"
      render={({ field }) => (
        <FlexibleDecisionCard
            title="Lean Towards Growth or Value"
            description="If you want some extra concentration in your portfolio to a specific area of the domestic equities market,
            you can choose to tilt towards growth or value stocks."
            field={field}
            options={[
              { label: 'Value', value: 'value_tilt' },
              { label: 'Growth', value: 'growth_tilt' },
              { label: 'No Tilt', value: null },
            ]}
        />
      )}
    />
  );
};


export {
    AssetAllocation, DividendIncomeYesNo, SingleStockSMAYesNo, InternationalEquitiesYesNo,
    PortfolioTilt

}
